// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';

// Utils
import { isInIFrame } from '../../../utils/services/iframe';
import { retrieveDeviceId } from '../../../device/deviceService';
import { authenticateWithApple, authenticateWithGoogle } from '../../authUtils';
import { getIsSwiftHybrid } from '../../../hybridApp/hybridAppUtils';

// Actions
import { login, logout } from '../../authActions';
import { redirectLoggedInUserToWorkspace } from '../../../reducers/navigationActions';

// Components
import FullScreenModal from '../../../components/modal/FullScreenModal';
import Icon from '../../../components/icons/Icon';
import RouteLink from '../../../components/buttons/RouteLink';
import LoginForm from './LoginForm';
import AuthFormDisclaimerSection from '../../AuthFormDisclaimerSection';
import SocialSignInButton from '../../SocialSignInButton';
import AppleLogo from '../../icons/AppleLogo';
import GoogleLogo from '../../icons/GoogleLogo';

// Constants
import { AUTH_FLOWS } from '../../../../common/utils/authConstants';

// Styles
import '../AuthModal.scss';

const showOverlay = (props) => get(['route', 'overlay'], props) || false;
const getRegisterPath = (props) => get(['route', 'registerPath'], props) || 'register';

const mapStateToProps = (state) => ({
    isGuest: state.getIn(['app', 'currentUser', 'isGuest']),
    deviceId: retrieveDeviceId(),
});

// Redux connect methods
const mapDispatchToProps = (dispatch) => ({
    dispatchLogin: ({ email, password, ...rest }) => dispatch(login(email, password, rest)),
    dispatchForceLogout: () => dispatch(logout()),
    navigateToWorkspace: () => dispatch(redirectLoggedInUserToWorkspace()),
});

@connect(mapStateToProps, mapDispatchToProps)
class Login extends React.Component {
    componentDidMount() {
        const { isGuest, navigateToWorkspace, dispatchForceLogout } = this.props;

        // on iPad hybrid app, always defer to native login, so force a logout if Login is mounted
        if (getIsSwiftHybrid()) return dispatchForceLogout();

        if (!isGuest) return navigateToWorkspace();
    }

    render() {
        const { location, close, deviceId } = this.props;

        if (isInIFrame()) return null;

        return (
            <FullScreenModal className="AuthModal" overlay={showOverlay(this.props)} close={close}>
                <div className="logo">
                    <Icon name="milanote-logo-medium" />
                </div>

                <div className="heading">
                    <h1>Log in to Milanote</h1>
                </div>

                <div className="section">
                    <LoginForm {...this.props} />
                </div>

                <div className="section">
                    <div className="social-buttons">
                        <SocialSignInButton
                            className="MilanoteGoogleSignIn"
                            logo={<GoogleLogo />}
                            onClick={() => authenticateWithGoogle({ flow: AUTH_FLOWS.SIGN_IN, deviceId })}
                        >
                            Log in with Google
                        </SocialSignInButton>

                        <SocialSignInButton
                            className="MilanoteAppleSignIn"
                            logo={<AppleLogo />}
                            onClick={() => authenticateWithApple({ flow: AUTH_FLOWS.SIGN_IN, deviceId })}
                        >
                            Log in with Apple
                        </SocialSignInButton>
                    </div>

                    <div className="register-instead">
                        Don&apos;t have an account?{' '}
                        <RouteLink to={`/${getRegisterPath(this.props)}`} keepParams location={location}>
                            Sign up now
                        </RouteLink>
                    </div>
                </div>

                <AuthFormDisclaimerSection />
            </FullScreenModal>
        );
    }
}

Login.propTypes = {
    location: PropTypes.object,
    deviceId: PropTypes.string,
    isGuest: PropTypes.bool,

    close: PropTypes.func,
    navigateToWorkspace: PropTypes.func,
    dispatchForceLogout: PropTypes.func,
};

export default Login;
